/**
 * GDS Judge.me integration — GraphQL-style client over Judge.me REST API v1
 * Docs: https://judge.me/api/docs
 */
(function (global) {
  'use strict';

  var API_BASE = 'https://api.judge.me/api/v1';

  var OPERATIONS = {
    WidgetSettings: { method: 'GET', path: '/widgets/settings' },
    HtmlMiracle: { method: 'GET', path: '/widgets/html_miracle' },
    ProductReviewWidget: { method: 'GET', path: '/widgets/product_review' },
    PreviewBadge: { method: 'GET', path: '/widgets/preview_badge' },
    SubmitReview: { method: 'POST', path: '/reviews', auth: false }
  };

  function JudgeMeGraphQL(config) {
    this.shopDomain = config.shopDomain || '';
    this.publicToken = config.publicToken || '';
    this.platform = config.platform || 'shopify';
  }

  JudgeMeGraphQL.prototype._buildUrl = function (path, variables) {
    var url = new URL(API_BASE + path);
    if (OPERATIONS[path.replace(API_BASE, '')]) {
      /* noop */
    }
    url.searchParams.set('shop_domain', this.shopDomain);
    if (this.publicToken) {
      url.searchParams.set('api_token', this.publicToken);
    }
    Object.keys(variables || {}).forEach(function (key) {
      var val = variables[key];
      if (val !== undefined && val !== null && val !== '') {
        url.searchParams.set(key, String(val));
      }
    });
    return url.toString();
  };

  JudgeMeGraphQL.prototype.query = function (operation, variables) {
    var op = OPERATIONS[operation];
    if (!op) {
      return Promise.reject(new Error('Unknown Judge.me operation: ' + operation));
    }
    if (op.auth !== false && !this.publicToken) {
      return Promise.reject(new Error('Judge.me public token is not configured'));
    }

    var url = API_BASE + op.path;
    var params = Object.assign({}, variables || {});
    var fullUrl = this._buildUrl(op.path, params);

    return fetch(fullUrl, {
      method: op.method,
      headers: op.method === 'POST' ? { 'Content-Type': 'application/json' } : {}
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok || data.error) {
          throw new Error(data.error || data.message || 'Judge.me request failed');
        }
        return data;
      });
    });
  };

  JudgeMeGraphQL.prototype.mutate = function (operation, input) {
    var op = OPERATIONS[operation];
    if (!op || op.method !== 'POST') {
      return Promise.reject(new Error('Unknown Judge.me mutation: ' + operation));
    }

    var body = Object.assign(
      {
        shop_domain: this.shopDomain,
        platform: this.platform
      },
      input || {}
    );

    // application/x-www-form-urlencoded avoids a CORS preflight that Judge.me rejects.
    var params = new URLSearchParams();
    Object.keys(body).forEach(function (key) {
      var val = body[key];
      if (val === undefined || val === null || val === '') return;
      params.append(key, String(val));
    });

    return fetch(API_BASE + op.path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        Accept: 'application/json'
      },
      body: params.toString()
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok || data.error) {
          throw new Error(data.error || data.message || 'Judge.me mutation failed');
        }
        return data;
      }).catch(function (err) {
        if (!res.ok) throw new Error('Could not submit review. Please try again.');
        if (res.ok) return { message: 'Review submitted' };
        throw err;
      });
    });
  };

  function extractWidgetHtml(data) {
    if (!data) return '';
    if (typeof data === 'string') return data;
    return data.widget || data.html || data.product_review_widget || data.preview_badge || '';
  }

  function injectSettingsHtml(html) {
    if (!html || typeof html !== 'string') return;
    var holder = document.createElement('div');
    holder.innerHTML = html;
    holder.querySelectorAll('script').forEach(function (script) {
      var el = document.createElement('script');
      if (script.src) {
        el.src = script.src;
        el.async = true;
      } else {
        el.textContent = script.textContent;
      }
      document.head.appendChild(el);
    });
    holder.querySelectorAll('style, link[rel="stylesheet"]').forEach(function (node) {
      document.head.appendChild(node.cloneNode(true));
    });
  }

  var loaderPromise = null;

  function loadJudgeMeLoader(cdnHost) {
    if (global.jdgm && global.jdgm._loaderExecuted) {
      return Promise.resolve();
    }
    if (loaderPromise) return loaderPromise;

    loaderPromise = new Promise(function (resolve) {
      // shopify_v2.js is already requested from gds-judgeme-config; also ensure loader.
      if (document.querySelector('script[src*="shopify_v2.js"], script[src*="widget_preloader"], script[src*="/loader.js"]')) {
        window.setTimeout(resolve, 200);
        return;
      }
      var script = document.createElement('script');
      script.src = (cdnHost || 'https://cdn.judge.me') + '/shopify_v2.js';
      script.async = true;
      script.setAttribute('data-cfasync', 'false');
      script.onload = function () { resolve(); };
      script.onerror = function () { resolve(); };
      document.head.appendChild(script);
    });

    return loaderPromise;
  }

  function activateWidgets() {
    if (global.jdgm && typeof global.jdgm.SHOP_DOMAIN === 'undefined' && global.GDS_JUDGEME) {
      global.jdgm = global.jdgm || {};
      global.jdgm.SHOP_DOMAIN = global.GDS_JUDGEME.shopDomain;
      global.jdgm.PLATFORM = 'shopify';
      global.jdgm.PUBLIC_TOKEN = global.GDS_JUDGEME.publicToken;
    }
    document.dispatchEvent(new CustomEvent('jdgm.widgetRefreshed'));
    if (global.jdgm && typeof global.jdgm.customBadgeRefresh === 'function') {
      global.jdgm.customBadgeRefresh();
    }
  }

  function revealJudgeMeWidgets(root) {
    var scope = root || document;
    try {
      scope.querySelectorAll('style.jdgm-temp-hiding-style').forEach(function (el) {
        el.remove();
      });
      scope.querySelectorAll('.jdgm-rev-widg').forEach(function (el) {
        if (el.style && el.style.display === 'none') el.style.display = '';
      });
    } catch (e) {}
  }

  function markReviewsMainEmptyState(section) {
    var widgetRoot = section && section.querySelector('[data-gds-jdgm-widget]');
    if (!widgetRoot) return;
    revealJudgeMeWidgets(section);

    var declared = section.getAttribute('data-gds-has-reviews');
    var revNodes = widgetRoot.querySelectorAll('.jdgm-rev');
    var attrCount = Number((widgetRoot.querySelector('.jdgm-rev-widg') || {}).getAttribute &&
      (widgetRoot.querySelector('.jdgm-rev-widg').getAttribute('data-number-of-reviews') || 0));
    var hasReviews = declared === 'true' || revNodes.length > 0 || attrCount > 0;
    var emptyCopy = /be the first to write a review|no reviews yet/i.test(widgetRoot.textContent || '');

    widgetRoot.classList.toggle('is-empty', !hasReviews || (emptyCopy && revNodes.length === 0));

    var note = section.querySelector('[data-gds-jdgm-summary-note]');
    if (note && !hasReviews) {
      note.classList.add('gds-jdgm-summary-note--empty');
      if (/verified buyers/i.test(note.textContent || '')) {
        note.textContent = 'Be the first to share your experience.';
      }
    }
  }

  function initProductReviews(section) {
    var cfg = global.GDS_JUDGEME || {};
    var client = new JudgeMeGraphQL(cfg);
    var widgetRoot = section.querySelector('[data-gds-jdgm-widget]');
    var badgeRoot = section.querySelector('[data-gds-jdgm-summary-badge]');
    var statusEl = section.querySelector('[data-gds-jdgm-status]');
    var formEl = section.querySelector('[data-gds-jdgm-form]');
    var existingWidget = section.querySelector('#judgeme_product_reviews.jdgm-review-widget');

    if (existingWidget && existingWidget.innerHTML.trim().length > 20) {
      if (statusEl) {
        statusEl.hidden = true;
        statusEl.textContent = '';
      }
      revealJudgeMeWidgets(section);
      loadJudgeMeLoader(cfg.cdnHost).then(function () {
        activateWidgets();
        revealJudgeMeWidgets(section);
        markReviewsMainEmptyState(section);
      });
      bindFallbackForm(formEl, cfg);
      markReviewsMainEmptyState(section);
      window.setTimeout(function () {
        revealJudgeMeWidgets(section);
        markReviewsMainEmptyState(section);
      }, 600);
      return;
    }

    if (!cfg.publicToken) {
      if (statusEl) statusEl.hidden = true;
      bindFallbackForm(formEl, cfg);
      markReviewsMainEmptyState(section);
      return;
    }

    var productVars = {
      handle: cfg.product && cfg.product.handle,
      external_id: cfg.product && cfg.product.id
    };

    client
      .query('WidgetSettings')
      .then(function (settingsData) {
        injectSettingsHtml(extractWidgetHtml(settingsData));
        return client.query('HtmlMiracle').catch(function () { return null; });
      })
      .then(function (miracleData) {
        if (miracleData) {
          var miracleHtml = extractWidgetHtml(miracleData);
          if (miracleHtml && widgetRoot) {
            var miracleSlot = widgetRoot.querySelector('[data-gds-jdgm-miracle]');
            if (miracleSlot) miracleSlot.innerHTML = miracleHtml;
          }
        }
        return Promise.all([
          client.query('ProductReviewWidget', productVars),
          client.query('PreviewBadge', productVars).catch(function () { return null; })
        ]);
      })
      .then(function (results) {
        var widgetHtml = extractWidgetHtml(results[0]);
        var badgeHtml = results[1] ? extractWidgetHtml(results[1]) : '';

        if (badgeRoot && badgeHtml) {
          badgeRoot.innerHTML = badgeHtml;
        }

        if (widgetRoot && widgetHtml) {
          var target = widgetRoot.querySelector('[data-gds-jdgm-widget-body]') || widgetRoot;
          target.innerHTML = widgetHtml;

          var legacy = widgetRoot.querySelector('#judgeme_product_reviews, .jdgm-review-widget');
          if (!legacy) {
            var wrap = document.createElement('div');
            wrap.id = 'judgeme_product_reviews';
            wrap.className = 'jdgm-widget jdgm-review-widget';
            wrap.setAttribute('data-product-id', cfg.product.id);
            wrap.setAttribute('data-product-title', cfg.product.title || '');
            wrap.innerHTML = widgetHtml;
            target.appendChild(wrap);
          }
        }

        return loadJudgeMeLoader(cfg.cdnHost);
      })
      .then(function () {
        activateWidgets();
        if (statusEl) statusEl.hidden = true;
        markReviewsMainEmptyState(section);
        window.setTimeout(function () { markReviewsMainEmptyState(section); }, 800);
      })
      .catch(function (err) {
        console.warn('[GDS Judge.me]', err);
        if (statusEl) statusEl.hidden = true;
        bindFallbackForm(formEl, cfg);
        markReviewsMainEmptyState(section);
      });
  }

  function initPreviewBadge(root) {
    var cfg = global.GDS_JUDGEME || {};
    if (!root) return;
    // Do not fetch/show preview badges for products with zero reviews.
    if (cfg.product && Number(cfg.product.reviewCount || 0) <= 0) {
      root.innerHTML = '';
      return;
    }
    if (!cfg.publicToken) return;

    var client = new JudgeMeGraphQL(cfg);
    client
      .query('PreviewBadge', {
        handle: cfg.product && cfg.product.handle,
        external_id: cfg.product && cfg.product.id
      })
      .then(function (data) {
        var html = extractWidgetHtml(data);
        if (!html) return;
        root.innerHTML = html;
        return loadJudgeMeLoader(cfg.cdnHost);
      })
      .then(function () {
        activateWidgets();
      })
      .catch(function (err) {
        console.warn('[GDS Judge.me] Preview badge:', err);
      });
  }

  function bindFallbackForm(formEl, cfg) {
    if (!formEl || formEl.getAttribute('data-gds-jdgm-bound') === '1') return;
    formEl.setAttribute('data-gds-jdgm-bound', '1');

    var ratingInput = formEl.querySelector('[name="rating"]');
    var starWrap = formEl.querySelector('[data-gds-jdgm-stars]');
    var stars = starWrap ? starWrap.querySelectorAll('[data-rating]') : [];

    function paint(value) {
      stars.forEach(function (btn) {
        var score = Number(btn.getAttribute('data-rating'));
        var on = score <= Number(value);
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-checked', score === Number(value) ? 'true' : 'false');
      });
    }

    if (ratingInput) {
      paint(ratingInput.value || 5);
      stars.forEach(function (btn) {
        btn.addEventListener('click', function () {
          ratingInput.value = btn.getAttribute('data-rating');
          paint(ratingInput.value);
        });
      });
    }

    formEl.addEventListener('submit', function (e) {
      e.preventDefault();
      var client = new JudgeMeGraphQL(cfg);
      var status = formEl.querySelector('[data-gds-jdgm-form-status]');
      var submitBtn = formEl.querySelector('[type="submit"]');
      var fd = new FormData(formEl);
      var name = String(fd.get('name') || '').trim();
      var email = String(fd.get('email') || '').trim();
      var body = String(fd.get('body') || '').trim();
      var rating = Number(fd.get('rating') || 0);

      if (!name || !email || !body || rating < 1 || rating > 5) {
        if (status) {
          status.hidden = false;
          status.textContent = 'Please add your name, email, rating, and review.';
          status.className = 'gds-jdgm-form-status is-error';
        }
        return;
      }

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.setAttribute('aria-busy', 'true');
      }
      if (status) {
        status.hidden = false;
        status.textContent = 'Submitting your review…';
        status.className = 'gds-jdgm-form-status';
      }

      client
        .mutate('SubmitReview', {
          id: cfg.product && cfg.product.id,
          name: name,
          email: email,
          rating: rating,
          title: String(fd.get('title') || '').trim(),
          body: body
        })
        .then(function () {
          if (status) {
            status.hidden = false;
            status.textContent = 'Thank you! Your review was submitted and will appear after moderation.';
            status.className = 'gds-jdgm-form-status is-success';
          }
          formEl.reset();
          if (ratingInput) {
            ratingInput.value = '5';
            paint(5);
          }
          window.setTimeout(function () {
            var modal = document.getElementById('gds-review-modal');
            if (modal) {
              modal.hidden = true;
              document.body.style.overflow = '';
            }
          }, 1600);
        })
        .catch(function (err) {
          if (status) {
            status.hidden = false;
            status.textContent = err.message || 'Could not submit review. Please try again.';
            status.className = 'gds-jdgm-form-status is-error';
          }
        })
        .finally(function () {
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.removeAttribute('aria-busy');
          }
        });
    });
  }

  function initAll() {
    // Heal sticky CDN shells that still claim verified buyers with no reviews.
    document.querySelectorAll('[data-gds-jdgm-reviews], #reviews.pdp-reviews-band, .gds-pdp-reviews').forEach(function (section) {
      var note = section.querySelector('[data-gds-jdgm-summary-note], .gds-jdgm-summary-note');
      var hasAttr = section.getAttribute('data-gds-has-reviews');
      var count = Number(section.getAttribute('data-gds-review-count') || 0);
      var revNodes = section.querySelectorAll('.jdgm-rev');
      var hasReviews = hasAttr === 'true' || count > 0 || revNodes.length > 0;
      if (note && !hasReviews && (/verified buyers/i.test(note.textContent || '') || !(note.textContent || '').trim())) {
        note.textContent = 'Be the first to share your experience.';
        note.classList.add('gds-jdgm-summary-note--empty');
      }
      var main = section.querySelector('[data-gds-jdgm-widget], .gds-jdgm-reviews-main');
      if (main && !hasReviews && !main.querySelector('.jdgm-rev-widg, [data-gds-jdgm-empty-state]')) {
        var empty = document.createElement('div');
        empty.className = 'gds-jdgm-empty-state';
        empty.setAttribute('data-gds-jdgm-empty-state', '');
        empty.innerHTML = '<p>Be the first to share your experience.</p>';
        main.appendChild(empty);
      }
      revealJudgeMeWidgets(section);
    });

    document.querySelectorAll('[data-gds-jdgm-reviews]').forEach(initProductReviews);
    document.querySelectorAll('[data-gds-jdgm-preview-badge]').forEach(initPreviewBadge);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  global.GDSJudgeMe = {
    JudgeMeGraphQL: JudgeMeGraphQL,
    initProductReviews: initProductReviews,
    initPreviewBadge: initPreviewBadge
  };
})(window);
